import { isNil } from 'ramda';

import type { CurrentTenantId } from '@/common/overmind/namespaces/tenants';
import type {
  DomainGuid,
  RawData
} from '@/features/execute-calculation-set/overmind/calculation';
import { UnexpectedStateError } from '@/overmind/errors';

import type { ArcgisMapAdapter } from '../adapters/ArcgisMapAdapter/ArcgisMapAdapter';
import type { FeatureWithIDs } from '../adapters/baseadapter';

export class TenantId {
  private value: string;

  static from(tenantId: CurrentTenantId) {
    if (!tenantId || (tenantId && tenantId.type === 'NO_TENANT_SELECTED')) {
      throw new UnexpectedStateError();
    }

    return new TenantId(tenantId.context.tenantId);
  }

  private constructor(tenantId: string) {
    this.value = tenantId;
  }

  toString() {
    return this.value;
  }
}

type GetLayerDataElements = () => ReturnType<
  ArcgisMapAdapter['getLayerDataElements']
>;
type FindFeature = (objectId: DomainGuid) => FeatureWithIDs | undefined;
let _getGridElementDetails: GetLayerDataElements;
let _findFeature: FindFeature;

type GridElementDetailsApiInitializationContext = {
  getGridElementDetails: GetLayerDataElements;
  findFeature: FindFeature;
};

export type GridElementDetailsApi = {
  initialize: (context: GridElementDetailsApiInitializationContext) => void;
  getBy: (
    tenantId: TenantId,
    gridElementId: DomainGuid,
    gridElementType: GridElementType,
    controller?: AbortController
  ) => Promise<RawData>;
};

export class GridElementType {
  private constructor(private _layerId: number) {}

  static from(layerId: number) {
    return new GridElementType(layerId);
  }

  is(type: string) {
    return this._layerId.toString() === type;
  }
}
const gridElementDetails: GridElementDetailsApi = {
  initialize({ getGridElementDetails, findFeature }) {
    _getGridElementDetails = getGridElementDetails;
    _findFeature = findFeature;
  },
  async getBy(
    tenantId: TenantId,
    objectId: DomainGuid,
    gridElementType: GridElementType,
    __?: AbortController
  ) {
    if (!tenantId) {
      throw new UnexpectedStateError();
    }

    if (!_getGridElementDetails || !_findFeature) return { type: 'NO_DETAILS' };

    const response = await _getGridElementDetails();
    const feature = _findFeature(objectId);

    return toGridElementDetails(
      feature,
      toNamesMapping(gridElementType, response)
    );
  }
};

type UtilityNetworkNameToAliasMapping =
  | { type: 'NO_MAPPING_AVAILABLE' }
  | { type: 'MAPPING_AVAILABLE'; data: Record<string, string> };

function toNamesMapping(
  gridElementType: GridElementType,
  response: Awaited<ReturnType<GetLayerDataElements>>
): UtilityNetworkNameToAliasMapping {
  const layerDetails = response.layerDataElements.find(layer =>
    gridElementType.is(layer.dataElement.layerId.toString())
  );
  if (layerDetails) {
    return {
      type: 'MAPPING_AVAILABLE',
      data: Object.fromEntries(
        layerDetails.dataElement.fields.fieldArray.map(field => [
          field.name,
          field.aliasName
        ])
      )
    };
  }
  return { type: 'NO_MAPPING_AVAILABLE' };
}

function toGridElementDetails(
  feature: FeatureWithIDs | undefined,
  gridElementDetails: UtilityNetworkNameToAliasMapping
): RawData {
  if (gridElementDetails.type === 'NO_MAPPING_AVAILABLE')
    return { type: 'NO_DETAILS' };
  if (isNil(feature) || (feature && isNil(feature.properties)))
    return { type: 'NO_DETAILS' };
  return {
    type: 'GRID_ELEMENT_DETAILS',
    details: Object.fromEntries(
      Object.entries(gridElementDetails.data).map(([fieldName, aliasName]) => {
        // @ts-ignore
        return [aliasName, feature.properties[fieldName]];
      })
    )
  };
}

export default gridElementDetails;
